import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { getSyncRuns, queryKeys, triggerSync } from '@/api/fpa'
import type { SyncRun } from '@/api/types'

const POLL_MS = 2_000

/** Latest sync runs (admin only). Polls while the newest run is still running. */
export function useSyncRuns(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.syncRuns,
    queryFn: ({ signal }) => getSyncRuns(signal),
    enabled,
    staleTime: 30_000,
    refetchInterval: (query) => (query.state.data?.[0]?.status === 'running' ? POLL_MS : false),
  })
}

export function useTriggerSync() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: triggerSync,
    onSuccess: (run) => {
      queryClient.setQueryData<SyncRun[]>(queryKeys.syncRuns, (runs) => [
        run,
        ...(runs ?? []).filter((r) => r.id !== run.id),
      ])
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.syncRuns }),
  })
}

/** When a running sync finishes successfully, refetch the runway and every transaction list. */
export function useRefreshOnSyncComplete(latest: SyncRun | undefined): void {
  const queryClient = useQueryClient()
  const previous = useRef<SyncRun | undefined>(latest)
  useEffect(() => {
    const before = previous.current
    previous.current = latest
    if (before?.status === 'running' && latest?.id === before.id && latest.status === 'ok') {
      void queryClient.invalidateQueries({ queryKey: ['runway'] })
      void queryClient.invalidateQueries({ queryKey: ['transactions'] })
    }
  }, [latest, queryClient])
}
